/**
 * QuizKit Results — Step Circles Display + Q3 Audience Modifier
 * Paste into QuizKit -> Settings -> Custom JavaScript
 *
 * What it does:
 * 1. Listens for Q3 answer clicks during the quiz
 * 2. When the results page renders:
 *    - Detects which plan was assigned (Everyday / Enhanced / Maximum)
 *      via heading text OR product links in the hidden product grid
 *    - Hides the default QuizKit product grid
 *    - Renders step circle cards showing recommended steps + products
 *    - Each step links to the SSS page anchor (#step-1, #step-2, etc.)
 *    - Applies Q3 audience modifiers (family size wash, Kids Spray, messaging)
 *    - Shows bundle discount reminder
 */
(function () {
  'use strict';

  var SSS_PAGE = '/pages/incognito-simple-step-system';

  var STEP_LABELS = {
    1: 'Prepare',
    2: 'Protect',
    3: 'Soothe & Wear',
    4: 'Environment'
  };

  var RESULTS = {
    everyday: {
      steps: [1, 2, 4],
      products: {
        1: ['Hair + Body Wash'],
        2: ['Repellent Spray'],
        4: ['Incense Sticks', 'Room Refresher']
      }
    },
    enhanced: {
      steps: [1, 2, 3, 4],
      products: {
        1: ['Hair + Body Wash'],
        2: ['Repellent Spray'],
        3: ['Aftersun', 'Bracelet'],
        4: ['Incense Sticks', 'Room Refresher']
      }
    },
    maximum: {
      steps: [1, 2, 3, 4],
      products: {
        1: ['Hair + Body Wash'],
        2: ['Repellent Spray', 'Repellent Lotion'],
        3: ['Aftersun', 'Bracelet'],
        4: ['Incense Sticks', 'Room Refresher']
      }
    }
  };

  var Q3_ANSWERS = {
    SOLO: 'Just me',
    COUPLE: 'Couple / household',
    FAMILY: 'Family with children'
  };

  var selectedAudience = null;
  var hasApplied = false;

  // --- Helpers ---

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function strong(text) {
    var s = document.createElement('strong');
    s.textContent = text;
    return s;
  }

  function txt(str) {
    return document.createTextNode(str);
  }

  // --- Observe Q3 answer clicks ---

  function watchForQ3Clicks() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quiz-kit-key] button');
      if (!btn) return;
      var text = btn.textContent.trim();
      if (text === Q3_ANSWERS.SOLO) selectedAudience = 'solo';
      else if (text === Q3_ANSWERS.COUPLE) selectedAudience = 'couple';
      else if (text === Q3_ANSWERS.FAMILY) selectedAudience = 'family';

      // Reset if user clicks Retake Quiz
      if (text === 'Retake Quiz' || text === 'retake quiz') {
        hasApplied = false;
        selectedAudience = null;
      }
    }, true);
  }

  // --- Detect which result plan was shown ---

  function detectResult() {
    var wrapper = document.querySelector('[id="quiz-kit-result-advanced-wrapper"]');
    if (!wrapper) return 'maximum';

    // Method 1: check text content for plan name keywords
    var text = wrapper.textContent.toLowerCase();
    if (text.indexOf('everyday') !== -1) return 'everyday';
    if (text.indexOf('enhanced') !== -1) return 'enhanced';
    if (text.indexOf('maximum') !== -1) return 'maximum';

    // Method 2: detect from product links in the (hidden) products section
    var productLinks = wrapper.querySelectorAll('a[href*="/products/"]');
    var hasLotion = false;
    var hasAftersun = false;
    for (var i = 0; i < productLinks.length; i++) {
      var href = productLinks[i].getAttribute('href') || '';
      if (href.indexOf('insect-repellent-lotion') !== -1) hasLotion = true;
      if (href.indexOf('moisturiser-aftersun') !== -1) hasAftersun = true;
    }
    if (hasLotion) return 'maximum';
    if (hasAftersun) return 'enhanced';
    return 'everyday';
  }

  // --- Hide "Add all to Cart" button ---
  // Disabled per Incognito request (21 May 2026): button adds ~20 items due to a
  // bug. Hide for now — revisit when QuizKit cart logic is fixed.
  // Matches by visible text so we're not coupled to QuizKit's class names.

  function hideAddAllToCart(wrapper) {
    var clickables = wrapper.querySelectorAll('button, a, [role="button"]');
    for (var i = 0; i < clickables.length; i++) {
      var el = clickables[i];
      if (el.dataset && el.dataset.qkHidden) continue;
      var text = (el.textContent || '').trim().toLowerCase();
      // Loosened: any text containing "add all" AND "cart" (handles trailing
      // icons, arrows, case variations like "ADD ALL TO CART →").
      if (/add\s+all/.test(text) && /cart/.test(text)) {
        el.style.display = 'none';
        el.setAttribute('aria-hidden', 'true');
        if (el.dataset) el.dataset.qkHidden = 'cart-add';
      }
    }
  }

  // MutationObserver wrapper — re-runs the hide whenever QuizKit re-renders
  // the results area (so a late-rendered button still gets hidden).
  function watchAndHideAddAllToCart(wrapper) {
    hideAddAllToCart(wrapper);
    if (wrapper.dataset && wrapper.dataset.qkObserveAddAll) return;
    if (wrapper.dataset) wrapper.dataset.qkObserveAddAll = 'true';
    var observer = new MutationObserver(function () {
      hideAddAllToCart(wrapper);
    });
    observer.observe(wrapper, { childList: true, subtree: true });
  }

  // --- Fix "Retake quiz" reliability ---
  // T7/T8: QuizKit persists completion state, which causes the retake button
  // to sometimes fail AND causes return visitors to land directly on the
  // result screen. Wrap retake clicks: clear QuizKit-related storage, then
  // reload the page so the quiz starts fresh.

  function fixRetakeQuiz(wrapper) {
    var clickables = wrapper.querySelectorAll('button, a, [role="button"]');
    for (var i = 0; i < clickables.length; i++) {
      var el = clickables[i];
      if (el.dataset && el.dataset.qkRetakeWired) continue;
      var text = (el.textContent || '').trim().toLowerCase();
      if (/retake/.test(text) || /start\s+over/.test(text) || /take\s+again/.test(text)) {
        if (el.dataset) el.dataset.qkRetakeWired = 'true';
        el.addEventListener('click', function () {
          try {
            var clearStorage = function (storage) {
              if (!storage) return;
              var keys = [];
              for (var k = 0; k < storage.length; k++) {
                var key = storage.key(k);
                if (key && /quiz|qk[-_]|quizkit/i.test(key)) keys.push(key);
              }
              for (var m = 0; m < keys.length; m++) {
                storage.removeItem(keys[m]);
              }
            };
            clearStorage(window.localStorage);
            clearStorage(window.sessionStorage);
          } catch (err) { /* storage may be blocked; reload anyway */ }
          // Small delay so QuizKit's own handler (if any) runs first.
          setTimeout(function () { window.location.reload(); }, 100);
        });
      }
    }
  }

  // --- Build the "enough incognito" note (T1) ---

  function buildEnoughIncognitoNote() {
    var note = el('p', 'qk-incognito-note');
    note.appendChild(txt('Make sure you take enough incognito® for your trip – many local repellents don’t offer complete protection.'));
    return note;
  }

  // --- Inject CSS (once) ---

  function injectStyles() {
    if (document.getElementById('qk-steps-styles')) return;
    var style = document.createElement('style');
    style.id = 'qk-steps-styles';
    style.textContent =
      '.qk-steps-row{display:flex;gap:16px;justify-content:center;max-width:720px;margin:12px auto;}' +
      '.qk-step{flex:1;text-align:center;min-width:0;padding:16px 12px;border-radius:12px;background:#fff !important;}' +
      '.qk-step--inactive{opacity:0.45;}' +
      '.qk-step__circle{width:48px;height:48px;border-radius:50%;margin:0 auto 10px;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#fff;}' +
      '.qk-step__circle--filled{background:#20a64a !important;}' +
      '.qk-step__circle--empty{background:#d1d5db !important;color:#9ca3af;}' +
      '.qk-step__label{font-weight:700;font-size:14px;margin-bottom:6px;color:#1d1d1d;}' +
      '.qk-step__products{list-style:none;padding:0;margin:0 0 8px;font-size:13px;color:#4b5563;line-height:1.5;}' +
      '.qk-step__products li{margin-bottom:2px;}' +
      '.qk-step__skip{font-size:13px;color:#9ca3af;font-style:italic;margin-bottom:8px;}' +
      '.qk-step__link{display:inline-block;font-size:13px;font-weight:600;color:#20a64a;text-decoration:none;}' +
      '.qk-step__link:hover{text-decoration:underline;}' +
      '.qk-msg{text-align:center;padding:16px 24px;margin:30px auto 12px;font-size:15px;color:#166534;background:#f0fdf4 !important;border:2px solid #bbf7d0;border-radius:8px;max-width:600px;line-height:1.6;}' +
      '.qk-msg p{margin:10px 0 0;}' +
      '.qk-incognito-note{text-align:center;font-style:italic;color:#4b5563;font-size:14px;margin:8px auto 20px;max-width:620px;padding:0 24px;line-height:1.5;text-wrap:pretty;}' +
      '#quiz-kit-sections-container{padding:0 !important;}' +
      '[id="quiz-kit-result-advanced-wrapper"]{padding:0 !important;}' +
      '[id="quiz-kit-result-advanced-wrapper"] > div{padding-top:0 !important;padding-bottom:0 !important;}' +
      '@media(max-width:600px){.qk-steps-row{flex-wrap:wrap;}.qk-step{flex:1 1 calc(50% - 8px);min-width:calc(50% - 8px);}}';
    document.head.appendChild(style);
  }

  // --- Build step circles ---

  function buildStepCircles(resultKey) {
    var result = RESULTS[resultKey];
    var products = {};
    var s, p;

    // Deep clone products
    for (s in result.products) {
      if (result.products.hasOwnProperty(s)) {
        products[s] = result.products[s].slice();
      }
    }

    // Q3 audience modifiers
    if (selectedAudience === 'couple' || selectedAudience === 'family') {
      if (products[1]) {
        for (p = 0; p < products[1].length; p++) {
          if (products[1][p] === 'Hair + Body Wash') {
            products[1][p] = 'Hair + Body Wash (Family size)';
          }
        }
      }
    }
    if (selectedAudience === 'family' && products[2]) {
      products[2].push('Kids Spray');
    }

    var row = el('div', 'qk-steps-row');

    for (s = 1; s <= 4; s++) {
      var active = result.steps.indexOf(s) !== -1;

      var step = el('div', 'qk-step' + (active ? '' : ' qk-step--inactive'));

      // Circle with number
      var circle = el('div', 'qk-step__circle' + (active ? ' qk-step__circle--filled' : ' qk-step__circle--empty'), String(s));
      step.appendChild(circle);

      // Label: "Step N" (or "Core Step 2" for the repellent step) + line break + step name
      var label = el('div', 'qk-step__label');
      var stepHeader = (s === 2) ? 'Core Step 2' : 'Step ' + s;
      label.appendChild(txt(stepHeader));
      label.appendChild(document.createElement('br'));
      label.appendChild(txt(STEP_LABELS[s]));
      step.appendChild(label);

      // Products or skip message
      if (active && products[s]) {
        var ul = el('ul', 'qk-step__products');
        for (p = 0; p < products[s].length; p++) {
          ul.appendChild(el('li', null, products[s][p]));
        }
        step.appendChild(ul);

        var link = el('a', 'qk-step__link', 'View products \u2192');
        link.href = SSS_PAGE + '#step-' + s;
        step.appendChild(link);
      } else {
        step.appendChild(el('div', 'qk-step__skip', 'Not needed'));
      }

      row.appendChild(step);
    }

    return row;
  }

  // --- Build audience + discount message ---

  function buildMessage() {
    var msg = el('div', 'qk-msg');

    // Universal "longer trip" prompt (shown regardless of audience)
    msg.appendChild(strong('Longer trip?'));
    msg.appendChild(txt(' Consider adding an extra core Step 2 product so you don\u2019t run out.'));

    // Universal discount reminder
    var discountP = el('p');
    discountP.appendChild(txt('Remember to add all 4 steps to the cart and '));
    discountP.appendChild(strong('save 20%.'));
    discountP.appendChild(txt(' Your discount will be applied automatically.'));
    msg.appendChild(discountP);

    return msg;
  }

  // --- Apply modifications to results page ---

  function applyModifications() {
    if (hasApplied) return;

    var wrapper = document.querySelector('[id="quiz-kit-result-advanced-wrapper"]');
    if (!wrapper) return;

    // Check that the sections container is actually populated
    var sectionsContainer = wrapper.querySelector('#quiz-kit-sections-container');
    if (!sectionsContainer || sectionsContainer.children.length === 0) return;

    hasApplied = true;

    var resultKey = detectResult();

    injectStyles();

    // Hide the default product grid (belt and braces — CSS also hides it)
    var productsSection = wrapper.querySelector('[data-section-id="products_1"]');
    if (productsSection) productsSection.style.display = 'none';

    // Hide the "Add all to Cart" button (broken) and watch for late re-renders.
    watchAndHideAddAllToCart(wrapper);

    // Wrap "Retake quiz" so it reliably clears state + reloads.
    fixRetakeQuiz(wrapper);

    // Remove any previous step displays
    var existing = wrapper.querySelectorAll('.qk-steps-row, .qk-msg, .qk-incognito-note');
    for (var i = 0; i < existing.length; i++) existing[i].remove();

    // Build and insert: enough-incognito note, step circles, audience + discount message.
    var container = document.createDocumentFragment();
    container.appendChild(buildEnoughIncognitoNote());
    container.appendChild(buildStepCircles(resultKey));
    container.appendChild(buildMessage());

    // Insert after the heading/intro section
    var customSection = sectionsContainer.querySelector('.qk-layout-section--customSection');
    if (customSection && customSection.nextSibling) {
      sectionsContainer.insertBefore(container, customSection.nextSibling);
    } else if (customSection) {
      sectionsContainer.appendChild(container);
    } else {
      sectionsContainer.appendChild(container);
    }
  }

  // --- Stale result detection ---
  // On re-entry, QuizKit sometimes renders the result wrapper with an empty
  // sections container — leaving only "Add all to Cart" + "Retake quiz" visible
  // (no step cards, no recommendations). Detect that, clear state, reload once.
  // Uses a non-quiz-matching marker key so it survives our own storage clear.

  function maybeFixStaleResult(wrapper) {
    if (!wrapper) return;
    try {
      if (sessionStorage.getItem('incognito-stale-reset')) return; // already tried this session
      sessionStorage.setItem('incognito-stale-reset', '1');

      var clearStorage = function (storage) {
        if (!storage) return;
        var keys = [];
        for (var k = 0; k < storage.length; k++) {
          var key = storage.key(k);
          if (key && /quiz|qk[-_]|quizkit/i.test(key)) keys.push(key);
        }
        for (var m = 0; m < keys.length; m++) {
          storage.removeItem(keys[m]);
        }
      };
      clearStorage(window.localStorage);
      clearStorage(window.sessionStorage);

      setTimeout(function () { window.location.reload(); }, 50);
    } catch (e) {}
  }

  // --- Watch for results page to appear ---

  function watchForResults() {
    var observer = new MutationObserver(function () {
      var resultWrapper = document.querySelector('[id="quiz-kit-result-advanced-wrapper"]');
      if (resultWrapper && !hasApplied) {
        // Hide broken button and wire retake immediately — these work even if
        // the full apply can't run (stale re-entry state).
        watchAndHideAddAllToCart(resultWrapper);
        fixRetakeQuiz(resultWrapper);

        // Retry full apply a few times while QuizKit populates content.
        setTimeout(applyModifications, 300);
        setTimeout(applyModifications, 800);
        setTimeout(applyModifications, 1500);

        // Final fallback — if applyModifications never fired (sections empty),
        // we're on a stale re-entry. Clear state + reload to restart fresh.
        setTimeout(function () {
          if (!hasApplied) maybeFixStaleResult(resultWrapper);
        }, 2000);
      }

      // Detect retake — result wrapper disappears
      if (!resultWrapper && hasApplied) {
        hasApplied = false;
      }
    });

    var quizContainer = document.querySelector('[data-quiz-kit-key]');
    if (quizContainer) {
      observer.observe(quizContainer, { childList: true, subtree: true });
    }
  }

  // --- Init ---

  function init() {
    watchForQ3Clicks();
    watchForResults();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
